import { isPanoCompatibleAssetSet } from './assetSetVisualizationModel';


export interface IKnowledgeViewAssetTagResolution {
    visibleTagIDs: string[];
    hiddenTagIDs: string[];
    tagIDsByAssetSetID: { [assetSetID: string]: string[] };
    fallbackVisible: boolean;
}

export interface AssetSetDisplayTagDefinition {
    id: string;
    mode: AssetSetDisplayMode;
    tagIDs: string[];
    isAllAssetTags?: boolean;
}

export interface IAssetSetDisplayTagResolution {
    hiddenTagIDs: string[];
    tagIDsByAssetSetID: { [assetSetID: string]: string[] };
    fallbackHidden: boolean;
}

export const resolveAssetSetDisplayTags = (
    definitions: readonly AssetSetDisplayTagDefinition[],
    displayAllAssetTags = false,
): IAssetSetDisplayTagResolution => {
    const seen = new Set<string>();
    const hiddenTagIDs: string[] = [];
    const tagIDsByAssetSetID: { [assetSetID: string]: string[] } = {};
    let fallbackHidden = false;
    definitions.forEach(definition => {
        if (definition.isAllAssetTags) {
            fallbackHidden = definition.mode === 'hidden';
            return;
        }
        const winning = (definition.tagIDs ?? []).filter(tagID => {
            if (!tagID || seen.has(tagID)) return false;
            seen.add(tagID);
            return true;
        });
        tagIDsByAssetSetID[definition.id] = winning;
        if (definition.mode === 'hidden') hiddenTagIDs.push(...winning);
    });
    if (displayAllAssetTags) {
        definitions.filter(definition => definition.mode === 'hidden').forEach(definition => {
            tagIDsByAssetSetID[definition.id] = [];
        });
    }
    return { hiddenTagIDs: displayAllAssetTags ? [] : hiddenTagIDs, tagIDsByAssetSetID, fallbackHidden: !displayAllAssetTags && fallbackHidden };
};
export const resolveKnowledgeViewAssetTags = (
    knowledgeViewAssetSets: IKnowledgeViewAssetSet[] = [],
    assetSets: IAssetSet[] = [],
    candidateTagIDs: string[] = [],
    isPano = false,
    displayAllAssetTags = false,
): IKnowledgeViewAssetTagResolution => {
    const assetSetsByID = new Map(assetSets.map(assetSet => [assetSet.ID.toLocaleLowerCase(), assetSet]));
    const seenTagIDs = new Set<string>();
    const visibleTagIDs: string[] = [];
    const hiddenTagIDs: string[] = [];
    const tagIDsByAssetSetID: { [assetSetID: string]: string[] } = {};
    let fallbackVisible = false;

    [...knowledgeViewAssetSets]
        .sort((left, right) => (left?.Ord ?? 0) - (right?.Ord ?? 0))
        .forEach(knowledgeViewAssetSet => {
            const assetSet = assetSetsByID.get(knowledgeViewAssetSet.ID.toLocaleLowerCase());
            if (!assetSet || (isPano && !isPanoCompatibleAssetSet(assetSet))) return;

            if (assetSet.Type === AssetSetType.AllAssetTags) {
                fallbackVisible = !knowledgeViewAssetSet.Hidden;
                return;
            }
            if (assetSet.Type === AssetSetType.All3DAssets) return;

            const winningTagIDs = (assetSet.AssetTagIDs ?? []).filter(tagID => {
                if (!tagID || seenTagIDs.has(tagID)) return false;
                seenTagIDs.add(tagID);
                return true;
            });

            tagIDsByAssetSetID[knowledgeViewAssetSet.ID] = winningTagIDs;
            if (knowledgeViewAssetSet.Hidden) {
                hiddenTagIDs.push(...winningTagIDs);
            } else {
                visibleTagIDs.push(...winningTagIDs);
            }
        });

    if (displayAllAssetTags) {
        knowledgeViewAssetSets.filter(entry => entry.Hidden).forEach(entry => {
            tagIDsByAssetSetID[entry.ID] = [];
        });
    }
    return {
        visibleTagIDs: displayAllAssetTags ? candidateTagIDs : visibleTagIDs,
        hiddenTagIDs: displayAllAssetTags ? [] : hiddenTagIDs,
        tagIDsByAssetSetID,
        fallbackVisible: displayAllAssetTags || fallbackVisible,
    };
};

export const getHiddenVisualizationAssetTagIDs = (
    visibleAssetSetIDs: string[] = [],
    hiddenAssetSetIDs: string[] = [],
    assetSets: IAssetSet[] = []
): string[] => {
    const assetSetsByID = new Map(assetSets.map(assetSet => [assetSet.ID, assetSet]));
    const visibleTagIDs = new Set<string>();
    const hiddenTagIDs = new Set<string>();

    visibleAssetSetIDs.forEach(assetSetID => {
        assetSetsByID.get(assetSetID)?.AssetTagIDs?.forEach(tagID => visibleTagIDs.add(tagID));
    });
    hiddenAssetSetIDs.forEach(assetSetID => {
        assetSetsByID.get(assetSetID)?.AssetTagIDs?.forEach(tagID => hiddenTagIDs.add(tagID));
    });

    return Array.from(hiddenTagIDs).filter(tagID => !visibleTagIDs.has(tagID));
};

const ViewerHelpers = {

    resolveKnowledgeViewAssetTags,

    resolveAssetSetDisplayTags,
    getHiddenVisualizationAssetTagIDs,

    isAssetTagAnnotation: (ann: IAnnotationDisplayItem) => {
        return (ann?.AnnotationType === AnnotationType.AssetTag || ann?.AnnotationType === AnnotationType.AssetTagWithBox);
    },

    isViewerAnnotation: (ann: IAnnotationDisplayItem) => {
        return !ViewerHelpers.isAssetTagAnnotation(ann);
    },

    safelyCastToAssetTag: (ann: IAnnotationDisplayItem): IAssetTag => {
        if (ViewerHelpers.isAssetTagAnnotation(ann)) {
            return ann as IAssetTag;
        }
        return null;
    },

    safelyCastToViewAnnotationTag: (ann: IAnnotationDisplayItem): IViewAnnotationTag => {
        if (ViewerHelpers.isViewerAnnotation(ann)) {
            return ann as IViewAnnotationTag;
        }
        return null;
    },

    getAssetTagClassID: (tag: IAssetTag, facility?: IFacilityDetail): string | null => {
        if (tag?.AssetClass?.ID) {
            return tag.AssetClass.ID;
        }

        const asset = facility?.Assets?.find(assetIdentity => assetIdentity.ID === tag?.AssetID);
        return asset?.AssetClassID ?? null;
    },

    isPlacedAssetTag: (tag: IAssetTag): boolean => {
        return !!(tag?.Target3D || tag?.Target2D || tag?.TargetBoxMin || tag?.TargetBoxMax || tag?.View360ID);
    },

    getDisplayableAssetTagClassIDs: (
        tags: IAssetTag[],
        facility?: IFacilityDetail,
        selectedOntologyID?: string,
        filteredAssetIDs: string[] = []
    ): string[] => {
        const hiddenAssetIDs = new Set(filteredAssetIDs);
        const classIDs = tags
            .filter(tag => ViewerHelpers.isPlacedAssetTag(tag))
            .filter(tag => !selectedOntologyID || (!!tag.ApplicableOntologyIDs && tag.ApplicableOntologyIDs.indexOf(selectedOntologyID) >= 0))
            .filter(tag => !tag.AssetID || !hiddenAssetIDs.has(tag.AssetID))
            .map(tag => ViewerHelpers.getAssetTagClassID(tag, facility))
            .filter((id): id is string => !!id);

        return Array.from(new Set(classIDs));
    },

    filterAssetTagsByVisibleClasses: (tags: IAssetTag[], visibleAssetTagClasses: string[] | null, facility?: IFacilityDetail): IAssetTag[] => {
        if (!visibleAssetTagClasses) {
            return tags;
        }

        const visibleClassIDs = new Set(visibleAssetTagClasses);
        return tags.filter(tag => {
            const classID = ViewerHelpers.getAssetTagClassID(tag, facility);
            return !!classID && visibleClassIDs.has(classID);
        });
    },

}

export default ViewerHelpers;
